export type StageId = 'new' | 'intake_cm' | 'intake_psy' | 'followup_cm' | 'followup_psy';
export type Side = 'right' | 'left';
export type ClinicId = string;

// Identifier for a single (stage, side) cell, used as a Set element when
// the user multi-selects stage cells from the main view. Format: "intake_psy:left".
export type SegmentKey = string;
export const segmentKey = (stage: StageId, side: Side): SegmentKey => `${stage}:${side}`;

export interface TimeMeta {
  value: number;
  unit: 'days';
  semantic: 'until_meeting' | 'waiting';
  text: string;
  text_template: string;  // contains `{v}` placeholder — used to rebuild after clinic filter
  tooltip: string | null;
}

export interface Segment {
  clinic: ClinicId;
  count: number;
  avg_wait: number | null;  // per-clinic average waiting_time; null on finished segments
}

export interface SideData {
  label: string;
  count: number;
  time: TimeMeta | null;
  segments: Segment[];
}

export interface StageData {
  id: StageId;
  name: string;
  tooltip: string | null;
  total: number;
  pct_of_active: number;
  right: SideData;
  left: SideData;
}

export interface ClinicData {
  id: ClinicId;
  name: string;
  count: number;
  pct: number;
}

export interface PatientStatusData {
  as_of_date: string;
  total_active: number;
  finished: number;
  clinics: ClinicData[];
  finished_segments: Segment[];
  stages: StageData[];
  trend: null | { value_pct: number; direction: 'up' | 'down'; points: number[] };
}

export interface PatientRecord {
  internal_id: number;
  hm_id: string;
  clinic_group: ClinicId;
  created_date: string;
  waiting_time: number;          // days until the appointment (from today)
  days_in_stage?: number | null;  // days in the stage, from the previous milestone
  next_meeting_date: string | null;  // ISO YYYY-MM-DD, populated on LEFT side
  status: string | null;
  invitation_status: string | null;
  cm_name: string | null;
  psy_name: string | null;
  red_flag: boolean;
  red_flag_reasons: string[];
  is_waiting_approval: boolean;
}

export interface DrillDownData {
  stage_id: StageId;
  side: Side;
  stage_name: string;
  side_label: string;
  time_label: string;
  stage_time_label?: string;      // what days_in_stage counts from, e.g. "מאינטייק מנהל תיק"
  total: number;
  patients: PatientRecord[];
}

export interface DrillDownContext {
  stage: StageId;
  side: Side;
}
